/*
  <lp-offer-timer> - countdown and time progress for the Mid-page Offer + CTA
  section (sections/offer-cta.liquid).

  One element per section instance; all state lives on the element, so several
  sections on one page run independently. Both the countdown and the bar read
  the same two moments, data-start and data-deadline (ISO 8601), and derive
  everything from Date.now(): nothing is stored in the browser, nothing resets
  on refresh, nothing is random. The quantity scarcity bar is rendered by
  Liquid and never touched here - it has no relation to time.

  data-mode="random": pe prima vizita a sesiunii se trage o durata intreaga de
  secunde intre data-min-minutes si data-max-minutes; deadline-ul si startul
  se tin in sessionStorage sub data-timer-key, deci refresh-ul si paginile
  aceleiasi oferte continua acelasi countdown. La zero se opreste pe 00:00 si
  nu reporneste in sesiune (deadline-ul expirat ramane in storage); nu arata
  textul de expirare si nu ascunde secțiunea. Fara storage: countdown nou la
  fiecare incarcare, tot aleator.

  Defensive parsing: an invalid or empty deadline hides the countdown and the
  bar and starts no interval. An invalid start, or a start at or after the
  deadline, hides only the bar. When the deadline passes the digits show
  00:00:00, the expired text appears, the bar is full and the interval stops;
  with data-hide-expired the whole section is hidden at that moment.

  The tag is deliberately not <offer-countdown>: that name is already defined
  by assets/custom-countdown.js and would take over the element.
*/
(function () {
  'use strict';

  if (window.customElements && customElements.get('lp-offer-timer')) return;

  function pad(n) {
    return n < 10 ? '0' + n : String(n);
  }

  /* Doar ISO 8601: 2026-10-31T23:59:00+03:00, cu sau fara secunde, offset
     sau Z; se accepta si un spatiu in loc de T. Date.parse singur e prea
     ingaduitor in unele browsere: "31 octombrie" devine 31 octombrie dintr-un
     an oarecare, iar o greseala de tastare ar putea ascunde secțiunea. */
  var ISO = /^\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/;

  function parseMoment(value) {
    var v = String(value || '').trim();
    if (!ISO.test(v)) return NaN;
    var t = Date.parse(v.replace(' ', 'T'));
    return isFinite(t) ? t : NaN;
  }

  function isVisible(el) {
    return !!el && el.getClientRects().length > 0;
  }


  class LpOfferTimer extends HTMLElement {
    connectedCallback() {
      this.reduced = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

      /* CTA: legat inainte de orice validare de timp, ca sa mearga si fara
         countdown. Un singur listener per instanta, delegat pe element, deci
         supravietuieste re-render-ului din Theme Editor. */
      this.boundCtaClick = this.handleCtaClick.bind(this);
      this.addEventListener('click', this.boundCtaClick);
      this.boundCtaKey = this.handleCtaKey.bind(this);
      this.addEventListener('keydown', this.boundCtaKey);

      this.mode = this.dataset.mode === 'evergreen' || this.dataset.mode === 'random' ? this.dataset.mode : 'fixed';
      this.barRemaining = this.mode !== 'fixed';
      var initial = parseFloat(this.dataset.initialProgress);
      this.initialPct = isFinite(initial) ? Math.max(0, Math.min(100, initial)) : 65;
      this.resolveMoments();
      this.hideExpired = this.dataset.hideExpired === 'true';
      this.sectionEl = document.getElementById('shopify-section-' + (this.dataset.sectionId || ''));

      this.cd = this.querySelector('[data-lpo-countdown]');
      this.bar = this.querySelector('[data-lpo-time-bar]');
      this.units = null;
      if (this.cd) {
        this.units = {
          days: this.cd.querySelector('[data-unit="days"]'),
          hours: this.cd.querySelector('[data-unit="hours"]'),
          minutes: this.cd.querySelector('[data-unit="minutes"]'),
          seconds: this.cd.querySelector('[data-unit="seconds"]')
        };
        this.expiredEl = this.cd.querySelector('[data-lpo-expired]');
      }
      if (this.bar) {
        this.fill = this.bar.querySelector('[data-lpo-fill]');
        this.pctEl = this.bar.querySelector('[data-lpo-percent]');
        this.track = this.bar.querySelector('[role="progressbar"]');
        this.lastPct = -1;
        if (this.reduced && this.fill) this.fill.style.transition = 'none';
      }

      if (isNaN(this.deadline)) {
        if (this.cd) this.cd.hidden = true;
        if (this.bar) this.bar.hidden = true;
        return;
      }
      if (this.bar && (isNaN(this.start) || this.start >= this.deadline)) {
        this.bar.hidden = true;
        this.bar = null;
      }

      this.boundTick = this.tick.bind(this);
      this.tick();
      if (!this.done) {
        this.timerId = setInterval(this.boundTick, 1000);
        document.addEventListener('visibilitychange', this.boundTick);
      }
    }

    disconnectedCallback() {
      this.stop();
      if (this.boundCtaClick) this.removeEventListener('click', this.boundCtaClick);
      if (this.boundCtaKey) this.removeEventListener('keydown', this.boundCtaKey);
    }

    /* Sursa celor doua momente. fixed: parsare ISO, neschimbata.
       evergreen: la fiecare incarcare un countdown nou de `minutes`, fara
       niciun storage; refresh = de la capat. */
    resolveMoments() {
      if (this.mode === 'random') {
        this.resolveRandom();
        return;
      }
      if (this.mode !== 'evergreen') {
        this.deadline = parseMoment(this.dataset.deadline);
        this.start = parseMoment(this.dataset.start);
        return;
      }
      var minutes = parseFloat(this.dataset.minutes);
      if (!isFinite(minutes) || minutes < 1) minutes = 240;
      var duration = minutes * 60 * 1000;
      this.deadline = Date.now() + duration;
      this.start = this.deadline - duration;
    }

    /* random: o durata intreaga de secunde, uniform intre min si max minute
       (limite intregi, min >= 1, max >= min; altfel 10-20). Storage-ul tine
       { d: deadline, s: start } sub cheia ofertei; o intrare valida, chiar
       expirata, se refoloseste - asa 00:00 ramane 00:00 dupa refresh. */
    resolveRandom() {
      var min = Math.floor(parseFloat(this.dataset.minMinutes));
      var max = Math.floor(parseFloat(this.dataset.maxMinutes));
      if (!isFinite(min) || min < 1) min = 10;
      if (!isFinite(max) || max < 1) max = 20;
      if (max < min) { var t = min; min = max; max = t; }
      var key = 'lpo-timer:' + (this.dataset.timerKey || this.dataset.sectionId || 'offer');
      var stored = null;
      try { stored = JSON.parse(window.sessionStorage.getItem(key)); } catch (e) { stored = null; }
      if (stored && isFinite(stored.d) && isFinite(stored.s) && stored.s < stored.d) {
        this.deadline = stored.d;
        this.start = stored.s;
        return;
      }
      var minS = min * 60, maxS = max * 60;
      var seconds = minS + Math.floor(Math.random() * (maxS - minS + 1));
      this.start = Date.now();
      this.deadline = this.start + seconds * 1000;
      try { window.sessionStorage.setItem(key, JSON.stringify({ d: this.deadline, s: this.start })); } catch (e) { /* fara storage: countdown nou per incarcare */ }
    }

    /* Tastatura pentru CTA-ul EasySell fara href: un <a> cu role="button" nu
       primeste click nativ la Enter/Space, deci apelam aceeasi cascada. Cu
       href prezent nu intervenim: Enter da click nativ si cascada ruleaza
       prin handleCtaClick. */
    handleCtaKey(e) {
      if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
      var target = e.target && e.target.closest ? e.target.closest('[data-lpo-cta-action="easysell"]') : null;
      if (!target || !this.contains(target) || target.hasAttribute('href')) return;
      e.preventDefault();
      this.handleCtaClick({ target: target, preventDefault: function () {} });
    }

    scrollTo(el) {
      el.scrollIntoView({ behavior: this.reduced ? 'auto' : 'smooth', block: 'center' });
    }

    /* Cascada EasySell. preventDefault doar cand una dintre trepte chiar
       actioneaza; altfel href-ul ramane navigarea normala.
         1. window.openTysloForm - globalul aplicatiei, asignat in modul popup;
            acelasi apel il face butonul ei sticky.
         2. #easysell vizibil - modul embedded: scroll la formular.
         3. aplicatia inca neinitializata: scroll la punctul ei de montare,
            gasit generic, fara cheia blocului.
         4. nimic: navigare normala.
       Fara hook-urile interne ale aplicatiei pe acelasi element, deci un
       click = o deschidere. */
    handleCtaClick(e) {
      var target = e.target && e.target.closest ? e.target.closest('[data-lpo-cta-action="easysell"]') : null;
      if (!target || !this.contains(target)) return;

      if (typeof window.openTysloForm === 'function') {
        e.preventDefault();
        window.openTysloForm();
        return;
      }
      var form = document.getElementById('easysell');
      if (isVisible(form)) {
        e.preventDefault();
        this.scrollTo(form);
        return;
      }
      var mount = document.querySelector('[id*="easysell_cod_form_app_block"]') || document.querySelector('easysell-form');
      if (mount) {
        e.preventDefault();
        this.scrollTo(mount);
      }
    }

    stop() {
      if (this.timerId) clearInterval(this.timerId);
      this.timerId = 0;
      if (this.boundTick) document.removeEventListener('visibilitychange', this.boundTick);
    }

    renderUnits(totalSeconds) {
      if (!this.units) return;
      var u = this.units;
      var hours;
      if (u.days) {
        u.days.textContent = pad(Math.floor(totalSeconds / 86400));
        hours = Math.floor((totalSeconds % 86400) / 3600);
      } else {
        hours = Math.floor(totalSeconds / 3600);
      }
      if (u.hours) u.hours.textContent = pad(hours);
      /* Fara unitate de ore (random, MM:SS): minutele iau tot restul. */
      if (u.minutes) u.minutes.textContent = pad(u.hours ? Math.floor((totalSeconds % 3600) / 60) : Math.floor(totalSeconds / 60));
      if (u.seconds) u.seconds.textContent = pad(totalSeconds % 60);
    }

    renderBar(now) {
      if (!this.bar) return;
      /* fixed: cat din campanie a trecut, 0 -> 100. evergreen: timpul rămas
         scalat de la initialPct, deci porneste la 65% (default) si scade
         strict proporțional pana la 0% la zero; renderBar(this.deadline) da
         0 singur la expirare. */
      var span = this.deadline - this.start;
      var elapsed = now - this.start;
      var fraction = span > 0 ? (this.barRemaining ? span - elapsed : elapsed) / span : (this.barRemaining ? 0 : 1);
      var pct = this.barRemaining ? fraction * this.initialPct : fraction * 100;
      pct = Math.max(0, Math.min(100, pct));
      var rounded = Math.round(pct * 10) / 10;
      if (rounded === this.lastPct) return;
      this.lastPct = rounded;
      if (this.fill) this.fill.style.width = rounded + '%';
      if (this.track) this.track.setAttribute('aria-valuenow', String(Math.round(pct)));
      if (this.pctEl) this.pctEl.textContent = String(Math.round(pct));
    }

    expire() {
      this.done = true;
      this.renderUnits(0);
      if (this.bar) this.renderBar(this.deadline);
      this.stop();
      /* random: oferta nu expira efectiv - fara text de expirare, fara
         ascunderea sectiunii; countdown-ul ramane pe 00:00. */
      if (this.mode === 'random') return;
      if (this.expiredEl) this.expiredEl.hidden = false;
      if (this.hideExpired && this.sectionEl) this.sectionEl.style.display = 'none';
    }

    tick() {
      var now = Date.now();
      var remaining = this.deadline - now;
      if (remaining <= 0) {
        this.expire();
        return;
      }
      this.renderUnits(Math.ceil(remaining / 1000));
      this.renderBar(now);
    }
  }

  customElements.define('lp-offer-timer', LpOfferTimer);
})();
